import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Edit, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  ColDef,
  DomLayoutType,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';

import { ConfirmationModal } from '../shared/ConfirmationModal';
import { supabase } from '../../services/supabase';
import { useData } from '../../contexts/DataContext';
import { useNotification } from '../../contexts/NotificationContext';
import * as Sentry from '@sentry/react';
import DataTable from '../shared/DataTable';
import { formatDateTime } from '../../utils/dateUtils';
import { CustomerTargetGroup } from '../../types/data';

// Rows come from the shared CustomerTargetGroup, optionally joined with audit details.
type TargetGroup = CustomerTargetGroup;

interface TargetGroupFormData {
  name: string;
  description: string | null;
  domain_id: string;
  modify_comment: string | null;
}

interface TargetGroupsTabProps {
  domainId: string;
}

const TargetGroupsTab = ({ domainId }: TargetGroupsTabProps) => {
  const { customerTargetGroups, domains, fetchRelatedDomainData } = useData(); // Use the DataContext
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<TargetGroup | null>(null);
  const [formData, setFormData] = useState<TargetGroupFormData>({
    name: '',
    description: null,
    domain_id: domainId || '',
    modify_comment: null,
  });
  const { showNotification } = useNotification();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const domainExists = domains.some((d) => d.id === formData.domain_id);
    if (!domainExists) {
      showNotification('Domain not found', 'error');
      return;
    }

    const operation = selectedGroup
      ? supabase.from('customer_target_groups').update(formData).eq('id', selectedGroup.id)
      : supabase.from('customer_target_groups').insert([formData]);

    const { error } = await operation;

    if (error) {
      console.error('Error saving target group:', error);
      showNotification(t('target_group.error_saving_target_group'), 'error');
      Sentry.captureException(error);
    } else {
      setIsModalOpen(false);
      fetchRelatedDomainData(domainId);
      resetForm();
      showNotification(t('target_group.target_group_saved_successfully'), 'success');
    }
  };

  const handleDelete = async () => {
    if (!selectedGroup) return;

    const { error } = await supabase
      .from('customer_target_groups')
      .delete()
      .eq('id', selectedGroup.id);

    if (error) {
      console.error('Error deleting target group:', error);
      showNotification(t('target_group.error_deleting_target_group'), 'error');
      Sentry.captureException(error);
    } else {
      setIsDeleteModalOpen(false);
      fetchRelatedDomainData(domainId);
      setSelectedGroup(null);
      showNotification(t('target_group.target_group_deleted_successfully'), 'success');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: null, domain_id: domainId || '', modify_comment: null });
    setSelectedGroup(null);
  };

  const columnDefs: ColDef<TargetGroup>[] = [
    {
      field: 'name',
      headerName: t('common.name'),
      sortable: true,
      filter: true,
      onCellClicked: (params) => {
        setSelectedGroup(params.data as TargetGroup);
        setFormData(params.data as unknown as TargetGroupFormData);
        setIsModalOpen(true);
      },
      cellStyle: {
        cursor: 'pointer',
        color: '#3b82f6', // blue-500 color in Tailwind
      },
    },
    {
      field: 'description',
      headerName: t('common.description'),
      sortable: true,
      filter: true,
      tooltipField: 'description',
    },
    {
      field: 'domain_id',
      headerName: t('domain.domain'),
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        return domains.find((d) => d.id === params.value)?.domain || '';
      },
    },
    {
      field: 'modified_by_details',
      headerName: t('domain.modified_by'),
      sortable: true,
      filter: true,
      valueFormatter: (params: ValueFormatterParams<TargetGroup>) => {
        const details = params.data?.modified_by_details || { name: null, email: null };
        return details?.name || details?.email || '-';
      },
    },
    {
      field: 'updated_at',
      headerName: t('domain.last_updated'),
      sortable: true,
      filter: true,
      valueFormatter: (params: ValueFormatterParams<TargetGroup>) => {
        return formatDateTime(params.value);
      },
    },
    {
      headerName: t('common.actions'),
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<TargetGroup>) => {
        return (
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setSelectedGroup(params.data as TargetGroup);
                setFormData(params.data as unknown as TargetGroupFormData);
                setIsModalOpen(true);
              }}
              className="text-blue-500 hover:text-blue-700 mr-2"
            >
              <Edit className="h-5 w-5" />
            </button>
            <button
              onClick={() => {
                setSelectedGroup(params.data as TargetGroup);
                setIsDeleteModalOpen(true);
              }}
              className="text-red-500 hover:text-red-700"
            >
              <Delete className="h-5 w-5" />
            </button>
          </div>
        );
      },
    },
  ];

  const gridConfig = {
    pagination: true,
    paginationAutoPageSize: true,
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      resizable: true,
    },
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">{t('target_group.target_groups')}</h3>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          {t('target_group.add_target_group')}
        </button>
      </div>

      <div className="overflow-x-auto bg-white rounded-sm shadow flex-grow min-h-0">
        <DataTable<TargetGroup>
          rowData={customerTargetGroups}
          columnDefs={columnDefs}
          gridConfig={gridConfig}
          isLoading={false}
          theme="ag-theme-alpine"
          height={'100%'}
          noRowsMessage={t('target_group.no_target_groups')}
        />
      </div>

      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-lg p-6 max-w-3xl w-full">
            <Dialog.Title className="text-lg font-medium mb-4">
              {selectedGroup
                ? t('target_group.edit_target_group')
                : t('target_group.add_target_group')}
            </Dialog.Title>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block mb-1">{t('common.name')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block mb-1">{t('common.description')}</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows={10}
                />
              </div>
              <div>
                <label className="block mb-1">{t('domain.domain')}</label>
                <select
                  value={formData.domain_id}
                  onChange={(e) => setFormData({ ...formData, domain_id: e.target.value })}
                  className="w-full border rounded px-3 py-2 bg-gray-100"
                  required
                  disabled
                >
                  <option value="">{t('target_group.select_domain')}</option>
                  {domains.map((domain) => (
                    <option key={domain.id} value={domain.id}>
                      {domain.domain}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('target_group.comment')}
                </label>
                <textarea
                  value={formData.modify_comment || ''}
                  onChange={(e) => setFormData({ ...formData, modify_comment: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-2 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  {selectedGroup ? t('common.update') : t('common.create')}
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>

      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={t('target_group.confirm_delete')}
        message={t('')}
      />
    </div>
  );
};

export default TargetGroupsTab;
